import type { TimelineAnnotation } from "./AnnotationTimeline";

export const NAV_TIMELINE_TICK_BAND_PX = 14;
/** Total compact strip height — tick band is carved out of this budget (no net chrome growth). */
export const NAV_TIMELINE_COMPACT_HEIGHT_PX = 34;
export const NAV_TIMELINE_COMPACT_HEIGHT_NO_TICKS_PX = 34;
export const NAV_TIMELINE_PANEL_HEIGHT_PX = 120;
export const NAV_LABEL_MIN_SPACING_PX = 72;

const TICK_INTERVAL_CANDIDATES_SECONDS = [
  1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600, 7200, 14400, 21600,
  43200,
] as const;

function getDurationMinimumTickIntervalSeconds(totalDuration: number): number {
  if (totalDuration <= 5 * 60) return 30;
  if (totalDuration <= 60 * 60) return 60;
  if (totalDuration <= 6 * 60 * 60) return 5 * 60;
  return 15 * 60;
}

export function formatAbsoluteRecordingTime(seconds: number): string {
  const clamped = Math.max(0, Number.isFinite(seconds) ? seconds : 0);
  const hours = Math.floor(clamped / 3600);
  const minutes = Math.floor((clamped % 3600) / 60);
  const secs = Math.floor(clamped % 60);

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  }

  // Sub-hour elapsed study time: zero-padded MM:SS (01:00 = 1 minute, not 1 hour).
  return `${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

/** Wall-clock UTC label for a point on the navigation timeline. */
export function formatNavigationTimelineWallClockTime(
  elapsedSeconds: number,
  recordingStartMs: number,
): string {
  const clamped = Math.max(0, Number.isFinite(elapsedSeconds) ? elapsedSeconds : 0);
  const wall = new Date(recordingStartMs + clamped * 1000);
  const hours = wall.getUTCHours();
  const minutes = wall.getUTCMinutes();
  const seconds = wall.getUTCSeconds();
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

export function formatNavigationTimelineTickLabel(
  elapsedSeconds: number,
  recordingStartMs?: number | null,
): string {
  if (
    typeof recordingStartMs === "number" &&
    Number.isFinite(recordingStartMs) &&
    recordingStartMs > 0
  ) {
    return formatNavigationTimelineWallClockTime(
      elapsedSeconds,
      recordingStartMs,
    );
  }
  return formatAbsoluteRecordingTime(elapsedSeconds);
}

/**
 * Pick a tick interval that keeps labels readable for the available strip width.
 * Wider recordings and narrower canvases use coarser major ticks.
 */
export function chooseNavigationTickIntervalSeconds(
  totalDuration: number,
  canvasWidthPx = 800,
  minLabelSpacingPx = NAV_LABEL_MIN_SPACING_PX,
): number {
  const safeDuration = Math.max(1, totalDuration);
  const safeWidth = Math.max(1, canvasWidthPx);
  const durationFloor = getDurationMinimumTickIntervalSeconds(safeDuration);

  for (const interval of TICK_INTERVAL_CANDIDATES_SECONDS) {
    if (interval < durationFloor) continue;
    const tickCount = Math.floor(safeDuration / interval) + 1;
    const spacing = safeWidth / tickCount;
    if (spacing >= minLabelSpacingPx) {
      return interval;
    }
  }

  return TICK_INTERVAL_CANDIDATES_SECONDS[
    TICK_INTERVAL_CANDIDATES_SECONDS.length - 1
  ];
}

export function buildNavigationMajorTickTimes(
  totalDuration: number,
  tickInterval: number,
): number[] {
  const safeDuration = Math.max(0, totalDuration);
  const safeInterval = Math.max(1, tickInterval);
  const times: number[] = [];
  for (let time = 0; time <= safeDuration + 0.001; time += safeInterval) {
    times.push(time);
  }
  return times;
}

export function shouldDrawNavigationTickLabel(
  labelCenterX: number,
  labelWidth: number,
  previousLabelRightEdge: number,
  minGapPx = 8,
): boolean {
  const labelLeft = labelCenterX - labelWidth / 2;
  return labelLeft >= previousLabelRightEdge + minGapPx;
}

export function isDurationAnnotation(annotation: TimelineAnnotation): boolean {
  return (
    Number.isFinite(annotation.endTime) &&
    annotation.endTime > annotation.startTime + 0.05
  );
}

/** Recordings longer than this use a playhead-focused visible window on the strip. */
export const NAV_TIMELINE_AUTO_FOCUS_THRESHOLD_SECONDS = 3600;

/** Playhead position within the micro strip sliding window (0 = start, 1 = end). */
export const NAV_TIMELINE_PLAYHEAD_CENTER_RATIO = 0.5;

export const MACRO_OVERVIEW_ZOOM_1H_SECONDS = 3600;
export const MACRO_OVERVIEW_ZOOM_30M_SECONDS = 1800;
export const MACRO_OVERVIEW_ZOOM_MIN_SPAN_SECONDS = 300;

export type MacroOverviewZoomMode = "full" | "1h" | "30m" | "custom";

export const MACRO_OVERVIEW_ZOOM_STORAGE_KEY = "eeg-macro-overview-zoom-mode";
export const MACRO_OVERVIEW_CUSTOM_RANGE_STORAGE_KEY =
  "eeg-macro-overview-custom-range";
export const MACRO_OVERVIEW_COLLAPSED_STORAGE_KEY =
  "eeg-macro-overview-collapsed";
export const NAV_TIMELINE_TIME_TICKS_STORAGE_KEY =
  "eeg-navigation-time-ticks-visible";

export function readMacroOverviewCollapsed(
  _totalDuration = 0,
): boolean {
  try {
    const stored = localStorage.getItem(MACRO_OVERVIEW_COLLAPSED_STORAGE_KEY);
    if (stored === "true") return true;
    if (stored === "false") return false;
  } catch {
    // ignore storage errors
  }

  // Default expanded for all study lengths; only an explicit user collapse sticks.
  return false;
}

export function writeMacroOverviewCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(
      MACRO_OVERVIEW_COLLAPSED_STORAGE_KEY,
      String(collapsed),
    );
  } catch {
    // ignore storage errors
  }
}

export function readNavigationTimeTicksPreference(
  totalDuration = 0,
): boolean {
  try {
    const stored = localStorage.getItem(NAV_TIMELINE_TIME_TICKS_STORAGE_KEY);
    if (stored === "true") return true;
    if (stored === "false") return false;
  } catch {
    // ignore storage errors
  }

  return true;
}

export function writeNavigationTimeTicksPreference(visible: boolean): void {
  try {
    localStorage.setItem(NAV_TIMELINE_TIME_TICKS_STORAGE_KEY, String(visible));
  } catch {
    // ignore storage errors
  }
}

export interface NavigationTimelineVisibleRange {
  rangeStart: number;
  rangeEnd: number;
}

export function isNavigationTimelineSlidingWindow(
  totalDuration: number,
): boolean {
  return totalDuration > NAV_TIMELINE_AUTO_FOCUS_THRESHOLD_SECONDS;
}

export function readMacroOverviewZoomMode(): MacroOverviewZoomMode {
  try {
    const value = localStorage.getItem(MACRO_OVERVIEW_ZOOM_STORAGE_KEY);
    if (
      value === "full" ||
      value === "1h" ||
      value === "30m" ||
      value === "custom"
    ) {
      return value;
    }
  } catch {
    // ignore storage errors
  }
  // Default to the full recording so the overview strip shows the whole study.
  // Users can still switch to 1h / 30m / custom (persisted in localStorage).
  return "full";
}

export function readMacroOverviewCustomRange(): NavigationTimelineVisibleRange | null {
  try {
    const raw = localStorage.getItem(MACRO_OVERVIEW_CUSTOM_RANGE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as NavigationTimelineVisibleRange;
    if (
      Number.isFinite(parsed.rangeStart) &&
      Number.isFinite(parsed.rangeEnd) &&
      parsed.rangeEnd > parsed.rangeStart
    ) {
      return parsed;
    }
  } catch {
    // ignore storage errors
  }
  return null;
}

export function clampNavigationTimelineVisibleRange(
  range: NavigationTimelineVisibleRange,
  totalDuration: number,
  minSpanSeconds = MACRO_OVERVIEW_ZOOM_MIN_SPAN_SECONDS,
): NavigationTimelineVisibleRange {
  const safeDuration = Math.max(1, totalDuration);
  const minSpan = Math.min(safeDuration, Math.max(1, minSpanSeconds));
  let rangeStart = Math.max(
    0,
    Math.min(safeDuration - minSpan, range.rangeStart),
  );
  let rangeEnd = Math.min(safeDuration, range.rangeEnd);
  if (rangeEnd - rangeStart < minSpan) {
    rangeEnd = Math.min(safeDuration, rangeStart + minSpan);
    rangeStart = Math.max(0, rangeEnd - minSpan);
  }
  return { rangeStart, rangeEnd };
}

function resolveCenteredVisibleWindow(params: {
  totalDuration: number;
  playheadTime: number;
  focusSpan: number;
  centerRatio?: number;
}): NavigationTimelineVisibleRange {
  const totalDuration = Math.max(1, params.totalDuration);
  const playheadTime = Math.max(
    0,
    Math.min(
      totalDuration,
      Number.isFinite(params.playheadTime) ? params.playheadTime : 0,
    ),
  );
  const focusSpan = Math.min(
    totalDuration,
    Math.max(1, params.focusSpan),
  );
  const centerRatio = params.centerRatio ?? NAV_TIMELINE_PLAYHEAD_CENTER_RATIO;
  let rangeStart = Math.max(0, playheadTime - focusSpan * centerRatio);
  let rangeEnd = Math.min(totalDuration, rangeStart + focusSpan);
  rangeStart = Math.max(0, rangeEnd - focusSpan);
  return { rangeStart, rangeEnd };
}

/**
 * For long recordings, map the navigation strip to a sliding window around the
 * playhead so the red indicator moves visibly instead of staying at the origin.
 */
export function resolveNavigationTimelineVisibleRange(params: {
  totalDuration: number;
  playheadTime: number;
  viewportDuration: number;
}): NavigationTimelineVisibleRange {
  const totalDuration = Math.max(1, params.totalDuration);
  const playheadTime = Math.max(
    0,
    Math.min(totalDuration, Number.isFinite(params.playheadTime) ? params.playheadTime : 0),
  );
  const viewportDuration = Math.max(0.1, params.viewportDuration);

  if (totalDuration <= NAV_TIMELINE_AUTO_FOCUS_THRESHOLD_SECONDS) {
    return { rangeStart: 0, rangeEnd: totalDuration };
  }

  const minFocusSpanSeconds = 600;
  const focusSpan = Math.min(
    totalDuration,
    Math.max(
      minFocusSpanSeconds,
      viewportDuration * 24,
      totalDuration * 0.02,
    ),
  );

  return resolveCenteredVisibleWindow({
    totalDuration,
    playheadTime,
    focusSpan,
    centerRatio: NAV_TIMELINE_PLAYHEAD_CENTER_RATIO,
  });
}

export function resolveMacroOverviewVisibleRange(params: {
  totalDuration: number;
  playheadTime: number;
  zoomMode: MacroOverviewZoomMode;
  customRange?: NavigationTimelineVisibleRange | null;
}): NavigationTimelineVisibleRange {
  const totalDuration = Math.max(1, params.totalDuration);
  const playheadTime = Math.max(
    0,
    Math.min(
      totalDuration,
      Number.isFinite(params.playheadTime) ? params.playheadTime : 0,
    ),
  );

  if (
    params.zoomMode === "full" ||
    totalDuration <= NAV_TIMELINE_AUTO_FOCUS_THRESHOLD_SECONDS
  ) {
    return { rangeStart: 0, rangeEnd: totalDuration };
  }

  if (params.zoomMode === "custom" && params.customRange) {
    return clampNavigationTimelineVisibleRange(
      params.customRange,
      totalDuration,
    );
  }

  const focusSpan =
    params.zoomMode === "1h"
      ? MACRO_OVERVIEW_ZOOM_1H_SECONDS
      : params.zoomMode === "30m"
        ? MACRO_OVERVIEW_ZOOM_30M_SECONDS
        : MACRO_OVERVIEW_ZOOM_1H_SECONDS;

  return resolveCenteredVisibleWindow({
    totalDuration,
    playheadTime,
    focusSpan: Math.min(totalDuration, focusSpan),
    centerRatio: NAV_TIMELINE_PLAYHEAD_CENTER_RATIO,
  });
}
